<template>
  <VSpinner v-if="!product || isReturn" class="offset-top-16 offset-bottom-17" />
  <article v-else>
    <div :id="isSubscriptionBookedByExtern ? 'PRODUCT_ORDER_SUMMARY_FINALIZE' : 'PRODUCT_ORDER_SUMMARY'" class="container-fixed">
      <VProgress
        v-if="flags.progressBarEnabled"
        class="offset-top-2 offset-bottom-2"
        :steps="steps"
        :active="'substore.' + (isOptin ? 'optin_' : '') + 'progress_summary'"
      />

      <VOrderDescription :product="product" property-suffix="Summary" />

      <VSection v-if="product.texts.productRequirementsDetails" cluster label="substore.requirements">
        <p v-html="product.texts.productRequirementsDetails"><!-- #30 --></p>
      </VSection>
    </div>

    <div v-if="!isSubscriptionBookedByExtern && product.price" class="container-fluid grayed offset-top-1 offset-bottom-2">
      <div class="container-fixed">
        <section class="offset-top-1" style="padding-bottom: 6px">
          <VPriceDetails v-bind="product" />
        </section>
      </div>
    </div>

    <div v-if="product.texts.linkInfoText" class="container-fixed">
      <br />
      <section>
        <span class="pull left" v-html="product.texts.linkInfoText"></span>
        <div
          v-if="['Web2T', 'ZPI'].includes(flags.partnerName) || (flags.isGeneric && flags.isLoginRedirectRequired) || flags.subscriptionBookedByExtern"
          class="btn-sectioned pull-right"
        >
          <button type="button" class="btn btn-default" @click="onChange">
            {{ $t('substore.change') }}
          </button>
        </div>
      </section>
      <br />
      <hr />
    </div>

    <div class="container-fixed">
      <VForm
        :hyperlinks="product.hyperlinks"
        :texts="product.texts"
        :text-lists="product.textLists"
        :can-not-succeed="product.canNotSucceed"
        :hide-terms-and-conditions="isSubscriptionBookedByExtern"
        v-bind="flags"
        @submit="onSubmit"
      >
        <!-- #91 -->
        <p v-if="isOptin">{{ $t('substore.optin_order_text') }}</p>
        <p v-else-if="flags.obligationToPay">{{ $t('substore.order_text_obligation') }}</p>
        <p v-else>{{ $t('substore.order_text') }}</p>

        <div class="row">
          <section class="offset-top-2 pull-right offset-right-1 button-bar">
            <!-- #73 -->
            <button type="button" class="btn btn-clean" @click="onAbort">
              {{ $t('substore.abort') }}
            </button>

            <!-- #92 -->
            <span v-if="flags.canNotSucceed">{{ $t('substore.msg_order_currently_not_possible') }}</span>
            <button v-else type="submit" class="offset-left-1 btn btn-default btn-brand" :disabled="!isBookingAllowed">
              <template v-if="isOptin">{{ $t('substore.order_btn_optin') }}</template>
              <template v-else-if="flags.obligationToPay">{{ $t('substore.order_btn_obligation') }}</template>
              <template v-else-if="isSubscriptionBookedByExtern">{{ $t('substore.order_btn_finalize') }}</template>
              <template v-else>{{ $t('substore.order_btn') }}</template>
            </button>
          </section>
        </div>
      </VForm>

      <!-- #348 -->
      <transition name="slide" appear>
        <div v-if="orderInProgress" class="pull-right offset-top-1 text-emphased">
          <span role="status">{{ $t('substore.hint_text_order_in_progress') }}</span>
          <VSpinner class="offset-top-1" style="margin-right: 0" />
        </div>
      </transition>
    </div>

    <div class="container-fixed offset-top-6">
      <VFootnotes :footnotes="footnotes" />
    </div>
  </article>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import Proxy from '@/proxies/SubstoreOrderSummaryProxy';
import StockProxy from '@/proxies/SubstoreStockProxy';
import ReturnProxy from '@/proxies/SubstoreReturnFromExternalPartnerProxy';
import OpenIdProxy from '@/proxies/SubstoreReturnFromOpenIdConnectProxy';
import SubscriptionBookProxy from '@/proxies/SubstoreSubscriptionBookProxy';
import SubscriptionCancelProxy from '@/proxies/SubstoreSubscriptionCancelProxy';
import NormalTransformer from '@/transformers/ProductOrderSummaryTransformer';
import OptinTransformer from '@/transformers/ProductOrderSummaryOptinTransformer';
import Footnotes from '@/utils/Footnotes';
import TitleMixin from '@/mixins/title';
import OrderStepsMixin from '@/mixins/orderSteps';
import VSpinner from '@/components/Spinner';
import VSection from '@/components/Section';
import emitter from '@/plugins/emitter';
import {
  ERROR,
  PRODUCT_CATALOGUE,
  PRODUCT_ORDER_CONFIRMATION,
  PRODUCT_ORDER_CONFIRMATION_FINALIZE,
  PRODUCT_ORDER_CONFIRMATION_OPTIN,
  PRODUCT_ORDER_SELECTION,
  PRODUCT_ORDER_SUMMARY,
  PRODUCT_ORDER_SUMMARY_FINALIZE,
  PRODUCT_ORDER_SUMMARY_OPENID_RETURN,
  PRODUCT_ORDER_SUMMARY_OPTIN,
  PRODUCT_ORDER_SUMMARY_RETURN,
  STOCK,
} from '@route-types';

export default {
  name: 'ProductOrderSummary',

  components: {
    VSpinner,
    VSection,
    VProgress: () => import('@/components/Progress'),
    VOrderDescription: () => import('@/components/Substore/OrderDescription'),
    VPriceDetails: () => import('@/components/Substore/PriceDetails'),
    VFootnotes: () => import('@/components/UI/Footnotes'),
    VForm: () => import('./Form'),
  },

  mixins: [TitleMixin, OrderStepsMixin],

  props: {
    subscriptionTemplateId: {
      type: String,
      required: false,
      default: null,
    },

    originalSubscriptionTemplateId: {
      type: String,
      required: false,
      default: null,
    },

    orderCallId: {
      type: String,
      required: false,
      default: null,
    },

    fixedLineNumber: {
      type: String,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      product: null,
      footnotes: null,
      form: null,
      isEmailVerification: false,
      rescissionLong: false,
      orderInProgress: false,
      isOptin: this.$route.name === PRODUCT_ORDER_SUMMARY_OPTIN,
      isReturn: this.$route.name === PRODUCT_ORDER_SUMMARY_RETURN,
      isOpenId: this.$route.name === PRODUCT_ORDER_SUMMARY_OPENID_RETURN,
      isSubscriptionBookedByExtern: this.$route.name === PRODUCT_ORDER_SUMMARY_FINALIZE,
      positiveError: false,
      flags: {
        partnerName: null,
        progressBarEnabled: null,
        orderSelectionSkipped: null,
        obligationToPay: null,
        customerEmail: null,
        isEmailVerification: null,
        isOnlyReservationAllowed: null,
        isPurchaseRedirectRequired: null,
        thirdPartyAuthentication: null,
        isFinalizationAllowed: null,
        isCompleteOrderAllowed: null,
        subscriptionBookedByExtern: null,
        subscriptionBeginIsRunning: null,
        isFinalization: null,
        isResumingSubscription: null,
        isLoginRedirectRequired: null,
        isGeneric: null,
        logoutURL: null,
        authenticationURL: null,
      },
    };
  },

  computed: {
    ...mapGetters('system', ['hasAccessCatalogue', 'hasAccessStockOverview']),

    ...mapState('system', ['isDeeplink', 'deeplinkSubscriptionTemplateId', 'deeplinkUniqueSubscriptionId']),

    isBookingAllowed() {
      return this.product?.bookingAllowed && !this.orderInProgress;
    },
  },

  async created() {
    if (this.isReturn) {
      this.processExternalResponse(await new ReturnProxy().returnFromExternalPartner(this.$route.query));
      return;
    } else if (this.isOpenId) {
      this.processExternalResponse(await new OpenIdProxy().returnFromOpenIdConnect(this.$route.query));
      return;
    }

    if (this.isDeeplink && this.deeplinkSubscriptionTemplateId && this.isOptin && !this.subscriptionTemplateId) {
      const { data: { orderCallId } = {} } =
        (await new StockProxy().stockAction(this.deeplinkSubscriptionTemplateId, this.deeplinkUniqueSubscriptionId, 'optin')) || {};

      this.$router.replace({
        name: PRODUCT_ORDER_SUMMARY_OPTIN,
        params: {
          subscriptionTemplateId: this.deeplinkSubscriptionTemplateId,
          orderCallId,
        },
      });
      return;
    }

    if (this.isDeeplink) {
      this.$store.dispatch('system/resetDeeplink');
    }

    const response = await new Proxy({ optin: this.isOptin }).find(this.subscriptionTemplateId, this.orderCallId);

    if (response.head.fault !== null) throw new Error(response.head.fault);

    Object.keys(this.flags).forEach(key => {
      this.flags[key] = response.data[key];
    });

    this.footnotes = new Footnotes();
    const Transformer = this.isOptin ? OptinTransformer : NormalTransformer;
    this.product = Transformer.fetch(response.data.product, this.footnotes);
    emitter.emit('setBannerPicture', this.product.images.headerImageSummaryResourcePath); // optin prefix not needed. will be striped in transformer
    emitter.emit('setBannerHeadline', this.product.texts.headerTitleSummary);
    emitter.emit('setBannerSubheadline', this.product.texts.headerSubTitleSummary);

    let pageContentSuffix = 'bestellzusammenfassung';
    if (this.isOptin) pageContentSuffix += 'optin';
    this.$webtrekk.viewPageWithPayload(pageContentSuffix, {
      page_type: 'basket',
      shop_product_sku: this.subscriptionTemplateId,
      shop_product_status: 'add',
      shop_product_name: this.product.texts.productName,
      shop_product_quantity: 1,
      shop_product_price: this.product.price?.value,
    });
  },

  methods: {
    processExternalResponse(responseExternal) {
      if (responseExternal.head.fault !== null) throw new Error(responseExternal.head.fault);
      if (responseExternal.data.redirectURL) {
        window.location.href = responseExternal.data.redirectURL;
        return;
      }
      this.$router.replace({
        name: responseExternal.data.finalization ? PRODUCT_ORDER_SUMMARY_FINALIZE : PRODUCT_ORDER_SUMMARY,
        params: {
          subscriptionTemplateId: responseExternal.data.subscriptionTemplateId,
          orderCallId: responseExternal.data.orderCallId,
        },
      });
    },

    async onSubmit(values) {
      this.orderInProgress = true;

      const subscBookParams = {
        orderCallId: this.orderCallId,
        subscriptionTemplateId: this.subscriptionTemplateId,
        // since we have to pass the parameters, even though they might now be requested by the
        // user, we have to detect if they are not set and then replace the desired value.
        // (we also could always provide "true" I guess, because the form won't send otherwise)
        acceptTerms: values.terms_and_conditions !== undefined ? values.terms_and_conditions : true,
        acceptRescissionRights: values.conditions !== undefined ? values.conditions : true,
        customerEmail: values.email,
        optin: this.isOptin,
      };
      const response = await new SubscriptionBookProxy().create(subscBookParams);
      let positiveErrorCode;
      if (response.head.fault !== null) {
        if (response.head.fault.isPositiveError) {
          positiveErrorCode = response.head.fault.args.reasonInfo;
        } else {
          throw new Error(response.head.fault);
        }
      }
      if (positiveErrorCode === '102') {
        this.positiveError = true;
      }

      const params = {
        originalSubscriptionTemplateId: this.originalSubscriptionTemplateId,
        subscriptionTemplateId: this.subscriptionTemplateId,
        thirdPartyAuthentication: this.flags.thirdPartyAuthentication,
        confirmationInProgress: this.getInProgress(response),
        priceEncoded: this.product.price ? btoa(this.product.price.value) : null,
      };

      if (this.isSubscriptionBookedByExtern) {
        this.$router.push({
          name: PRODUCT_ORDER_CONFIRMATION_FINALIZE,
          params,
        });
      } else {
        this.$router.push({
          name: this.isOptin ? PRODUCT_ORDER_CONFIRMATION_OPTIN : PRODUCT_ORDER_CONFIRMATION,
          params,
        });
      }
    },

    getInProgress(response) {
      if (this.positiveError === true || response?.data?.confirmationInProgress) {
        return 'inProgress';
      }
      return null;
    },

    onAbort() {
      new SubscriptionCancelProxy().create();
      if (this.flags.subscriptionBookedByExtern /* = DescElemToBeSetToFinalize */ || this.isOptin) {
        if (this.hasAccessStockOverview) {
          this.$router.push({ name: STOCK });
        } else {
          this.$router.push({ name: ERROR, params: { code: 'CANCEL_TARGET_DIALOGUE_NOT_ALLOWED' } });
        }
      } else if (this.hasAccessCatalogue) {
        this.$router.push({ name: PRODUCT_CATALOGUE });
      } else {
        this.$router.push({ name: ERROR, params: { code: 'CANCEL_TARGET_DIALOGUE_NOT_ALLOWED' } });
      }
    },

    onChange() {
      if (this.flags.partnerName === 'Web2T') {
        this.$router.push({
          name: PRODUCT_ORDER_SELECTION,
          params: {
            fixedLineNumber: this.fixedLineNumber,
            subscriptionTemplateId: this.subscriptionTemplateId,
            originalSubscriptionTemplateId: this.originalSubscriptionTemplateId,
          },
        });
      } else {
        window.location.href = this.flags.logoutURL || this.flags.authenticationURL;
      }
    },

    ...mapActions('order', ['parseSubscriptionBookResponse']),
  },
};
</script>

<style scoped>
.button-bar > span {
  position: relative;
  top: 1px;
  display: inline-block;
}
</style>
